import {
  BIG_ONE,
  BIG_TWO,
  BIG_ZERO,
  isqrtBig,
  parseBigIntToken,
} from "@/lib/tool-kit/math/bigint";
import {
  divisorCount,
  type Factorization,
  factorize,
  listDivisors,
  MAX_FACTOR_INPUT,
} from "@/lib/tool-kit/math/primes";

/**
 * The classifications people look up for a whole number: parity, primality, squares and
 * cubes, triangular and Fibonacci membership, palindromes, and the divisor-sum trio
 * (perfect, abundant, deficient). Everything is exact BigInt arithmetic on 1 … 10^20.
 */

export const MAX_LISTED_DIVISORS = 240;

export type Classification = "perfect" | "abundant" | "deficient";

export interface Membership {
  is: boolean;
}

export interface NumberProperties {
  n: bigint;
  digits: number;
  digitSum: number;
  digitalRoot: number;
  even: boolean;
  isPrime: boolean;
  factorization: Factorization;
  /** The divisor figures need a complete factorization; null when it is not. */
  divisorCount: number | null;
  divisorSum: bigint | null;
  aliquotSum: bigint | null;
  classification: Classification | null;
  totient: bigint | null;
  squarefree: boolean | null;
  divisors: bigint[] | null;
  square: { is: boolean; floorRoot: bigint };
  cube: { is: boolean; floorRoot: bigint };
  powerOfTwo: { is: boolean; exponent: number };
  /** `index` is the largest k with T(k) ≤ n, so it is n's own index when `is` holds. */
  triangular: { is: boolean; index: bigint };
  fibonacci: {
    is: boolean;
    /** Every index k with F(k) = n (two of them for 1). */
    indices: number[];
    previous: bigint;
    next: bigint;
  };
  decimalPalindrome: boolean;
  reversed: string;
  binary: string;
  binaryPalindrome: boolean;
}

/** ⌊∛n⌋ by binary search on the bit length. */
export function icbrtBig(n: bigint): bigint {
  if (n < BIG_TWO) {
    return n;
  }
  let low = BIG_ONE;
  let high = BIG_TWO ** BigInt(Math.ceil(n.toString(2).length / 3) + 1);
  while (low < high) {
    const mid = (low + high + BIG_ONE) / BIG_TWO;
    if (mid * mid * mid <= n) {
      low = mid;
    } else {
      high = mid - BIG_ONE;
    }
  }
  return low;
}

function reverseText(text: string): string {
  return [...text].reverse().join("");
}

/** The Fibonacci numbers F(1), F(2), … up to the first one that exceeds n. */
function fibonacciUpTo(n: bigint): bigint[] {
  const values = [BIG_ONE];
  let previous = BIG_ZERO;
  let current = BIG_ONE;
  while (current <= n) {
    [previous, current] = [current, previous + current];
    values.push(current);
  }
  return values;
}

function fibonacciFacts(n: bigint): NumberProperties["fibonacci"] {
  const values = fibonacciUpTo(n);
  const indices: number[] = [];
  for (const [position, value] of values.entries()) {
    if (value === n) {
      indices.push(position + 1);
    }
  }
  const previous = [...values].reverse().find((v) => v < n) ?? BIG_ZERO;
  const next = values.find((v) => v > n) ?? BIG_ZERO;
  return { is: indices.length > 0, indices, previous, next };
}

function triangularFacts(n: bigint): NumberProperties["triangular"] {
  const root = isqrtBig(BigInt(8) * n + BIG_ONE);
  const exact = root * root === BigInt(8) * n + BIG_ONE;
  return { is: exact, index: (root - BIG_ONE) / BIG_TWO };
}

/** σ(n), φ(n) and squarefreeness from the prime powers. */
function multiplicativeFacts(factorization: Factorization) {
  let sigma = BIG_ONE;
  let totient = BIG_ONE;
  let squarefree = true;
  for (const { prime, exponent } of factorization.factors) {
    const power = prime ** BigInt(exponent);
    sigma *= (power * prime - BIG_ONE) / (prime - BIG_ONE);
    totient *= power - power / prime;
    squarefree = squarefree && exponent === 1;
  }
  return { sigma, totient, squarefree };
}

function classify(n: bigint, aliquot: bigint): Classification {
  if (aliquot === n) {
    return "perfect";
  }
  return aliquot > n ? "abundant" : "deficient";
}

function digitFacts(n: bigint) {
  const text = n.toString();
  const digitSum = [...text].reduce((sum, d) => sum + Number(d), 0);
  return {
    text,
    digitSum,
    digitalRoot: n === BIG_ZERO ? 0 : 1 + Number((n - BIG_ONE) % BigInt(9)),
  };
}

/** Every property of a whole number 1 ≤ n ≤ 10^20. */
export function analyseNumber(n: bigint): NumberProperties {
  const factorization = factorize(n);
  const { text, digitSum, digitalRoot } = digitFacts(n);
  const squareRoot = isqrtBig(n);
  const cubeRoot = icbrtBig(n);
  const binary = n.toString(2);
  const complete = factorization.complete;
  const { sigma, totient, squarefree } = multiplicativeFacts(factorization);
  const aliquot = sigma - n;
  const factors = factorization.factors;
  const isPrime = factors.length === 1 && factors[0].exponent === 1;
  const isPowerOfTwo = binary.replaceAll("0", "") === "1";
  return {
    n,
    digits: text.length,
    digitSum,
    digitalRoot,
    even: n % BIG_TWO === BIG_ZERO,
    isPrime,
    factorization,
    divisorCount: complete ? divisorCount(factors) : null,
    divisorSum: complete ? sigma : null,
    aliquotSum: complete ? aliquot : null,
    classification: complete ? classify(n, aliquot) : null,
    totient: complete ? totient : null,
    squarefree: complete ? squarefree : null,
    divisors: complete ? listDivisors(factors, MAX_LISTED_DIVISORS) : null,
    square: { is: squareRoot * squareRoot === n, floorRoot: squareRoot },
    cube: { is: cubeRoot * cubeRoot * cubeRoot === n, floorRoot: cubeRoot },
    powerOfTwo: { is: isPowerOfTwo, exponent: binary.length - 1 },
    triangular: triangularFacts(n),
    fibonacci: fibonacciFacts(n),
    decimalPalindrome: text === reverseText(text),
    reversed: reverseText(text),
    binary,
    binaryPalindrome: binary === reverseText(binary),
  };
}

/* ------------------------------------------------------------- reading */

export type WholeNumberRead =
  | { kind: "ok"; value: bigint }
  | { kind: "empty" | "invalid" | "zero" | "negative" | "too-large" };

const SEPARATORS = /[\s_  ]+/g;

export function readWholeNumber(raw: string): WholeNumberRead {
  const text = raw.replace(SEPARATORS, "");
  if (text === "") {
    return { kind: "empty" };
  }
  const value = parseBigIntToken(text);
  if (value === null) {
    return { kind: "invalid" };
  }
  if (value < BIG_ZERO) {
    return { kind: "negative" };
  }
  if (value === BIG_ZERO) {
    return { kind: "zero" };
  }
  return value > MAX_FACTOR_INPUT
    ? { kind: "too-large" }
    : { kind: "ok", value };
}
